import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
  CalendarIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  FilterOffIcon,
  ImageIcon
} from '../components/Icons';
import MessageBox from '../components/MessageBox';
import ModernCarousel from '../components/ModernCarousel';
import UnifiedLoader from '../components/UnifiedLoader';

const decodeHtmlEntities = (html = '') => {
  if (!html) return '';
  const cleaned = html.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');
  if (typeof DOMParser === 'undefined') return cleaned;
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleaned, 'text/html');
  return (doc.documentElement.textContent || '').replace(/\s+/g, ' ').trim();
};

const BlogCards = ({ header }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, API_URL, PUBLIC_API_URL, fetchPublicData, getAssetUrl } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const authorFilter = searchParams.get('author');

  const title = header?.title || 'Neuigkeiten & Blog';
  const subtitle = header?.subtitle || 'Erfahre mehr über unsere letzten Abenteuer, Aktionen und Geschichten aus dem Stammesleben.';

  // Expansion and Pagination State
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9); // Default 3 rows of 3 on desktop

  const [showMessageBox, setShowMessageBox] = useState(false);
  const [messageBoxContent, setMessageBoxContent] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const data = await fetchPublicData('/blog');
        setPosts(Array.isArray(data) ? data : []);
        setLoading(false);
      } catch (err) {
        console.error('Fehler beim Laden der Blogbeiträge:', err);
        setError('Blogbeiträge konnten nicht geladen werden.');
        setLoading(false);
      }
    };
    fetchPosts();
  }, [fetchPublicData]);

  const handleCloseMessageBox = () => {
    setShowMessageBox(false);
    setMessageBoxContent('');
    setShowLoginPrompt(false);
  };

  const handleLoginRedirect = () => {
    handleCloseMessageBox();
    localStorage.setItem('redirectAfterLogin', '/#blog-section');
    navigate('/login');
  };

  const handleLoadMore = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    } else {
      setVisibleCount(prev => prev + 9); // Load 3 more rows
    }
  };

  if (loading || error || !posts || posts.length === 0) {
    return null;
  }

  const displayedPosts = authorFilter
    ? posts.filter(post => post.author && post.author._id === authorFilter)
    : posts;

  const renderPostCard = (post) => {
    const placeholderText = encodeURIComponent(post.title || 'Blog');
    const placeholderBgColor = (post.backgroundColor || '#cccccc').replace('#', '');
    const placeholderTextColor = 'FFFFFF';
    const placeholderImageUrl = `https://placehold.co/800x600/${placeholderBgColor}/${placeholderTextColor}?text=${placeholderText}`;
    
    const firstImg = (Array.isArray(post.images) && post.images.length > 0)
      ? post.images[0]
      : post.imageUrl;
      
    let primaryImg = '';
    if (typeof firstImg === 'string') {
      primaryImg = firstImg;
    } else if (firstImg && typeof firstImg === 'object') {
      primaryImg = firstImg.url || firstImg.src || firstImg.path || firstImg.key || '';
    } else if (typeof post.imageUrl === 'string') {
      primaryImg = post.imageUrl;
    } else if (post.imageUrl && typeof post.imageUrl === 'object') {
      primaryImg = post.imageUrl.url || post.imageUrl.src || post.imageUrl.path || post.imageUrl.key || '';
    }
      
    const imageUrl = getAssetUrl(primaryImg, { width: 800, quality: 85 }) || placeholderImageUrl;
    const plainContent = decodeHtmlEntities(post.content || '');
    const previewText = plainContent.replace(/\s+/g, ' ').trim();
    const excerpt = previewText ? `${previewText.substring(0, 140)}...` : '';
    const readingTime = Math.max(1, Math.ceil(previewText.split(/\s+/).length / 200));

    const authorName = post.author?.userData?.scoutName
      || post.author?.userData?.displayName
      || post.author?.userData?.firstName
      || (typeof post.author === 'string' && post.author !== '[object Object]' ? post.author : null)
      || 'Pfadfinder';

    const hasMultipleImages = Array.isArray(post.images) && post.images.length > 1;

    return (
      <div key={post._id} className="h-full flex flex-col">
        <div 
          onClick={() => navigate(`/blog/${post._id}`)}
          className="md3-card-elevated group relative flex flex-col h-full transition-all duration-medium2 rounded-[2rem] overflow-hidden border border-outlineVariant/30 hover:border-primary/30 cursor-pointer"
        >
          {/* Image Area */}
          <div className="relative aspect-video overflow-hidden bg-surface-containerHighest">
            <img
              src={imageUrl}
              alt={post.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-slow2 group-hover:scale-105"
            />
            <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap max-w-[80%]">
              <span className="bg-primary-container/90 backdrop-blur-md text-primary-onContainer border border-primary/20 text-label-small px-3 py-1 rounded-full font-bold shadow-sm">
                Blog
              </span>
              {post.linkedGroup && (
                <span
                  className="backdrop-blur-md text-label-small px-3 py-1 rounded-full font-bold shadow-sm truncate max-w-[130px]"
                  style={{
                    backgroundColor: `${post.linkedGroup.color || '#4CAF50'}30`,
                    color: post.linkedGroup.color || '#4CAF50',
                    border: `1px solid ${post.linkedGroup.color || '#4CAF50'}60`
                  }}
                >
                  {post.linkedGroup.name}
                </span>
              )}
              {post.linkedEvent && (
                <span className="bg-tertiary-container/90 backdrop-blur-md text-tertiary-onContainer text-label-small px-3 py-1 rounded-full font-bold shadow-sm">
                  Aktion
                </span>
              )}
            </div>
            {hasMultipleImages && (
              <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md text-white text-label-small font-semibold shadow-sm">
                <ImageIcon sx={{ fontSize: 13 }} />
                <span>{post.images.length} Fotos</span>
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="p-6 flex flex-col flex-grow">
            <div className="flex items-center justify-between gap-2 text-label-small text-surface-onVariant mb-3">
              <span className="font-semibold text-primary">{authorName}</span>
              <span>• {readingTime} Min. Lesezeit</span>
            </div>

            <h3 className="text-headline-small font-bold text-surface-on mb-3 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
              {post.title}
            </h3>

            <p className="text-body-medium text-surface-onVariant line-clamp-3 mb-6 flex-grow break-words overflow-hidden leading-relaxed">
              {excerpt}
            </p>

            {/* Footer Info */}
            <div className="flex items-center justify-between pt-4 border-t border-outlineVariant/40 mt-auto">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-secondary-container flex items-center justify-center text-secondary-onContainer">
                  <CalendarIcon style={{ fontSize: 14 }} />
                </div>
                <span className="text-label-small font-medium text-surface-onVariant">
                  {new Date(post.createdAt).toLocaleDateString('de-DE')}
                </span>
              </div>

              <span className="md3-btn-text gap-2 text-primary group-hover:gap-3 transition-all font-bold text-label-large">
                Mehr lesen <ArrowRightIcon style={{ fontSize: 18 }} />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const hasMore = isExpanded ? displayedPosts.length > visibleCount : displayedPosts.length > 3;

  return (
    <section className="py-16 bg-surface section-reveal delay-250 scroll-mt-28" id="blog-section">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="space-y-2">
            <h2 className="text-display-small font-bold text-surface-on">
              {title}
            </h2>
            <p className="text-body-large text-surface-onVariant max-w-xl">
              {subtitle}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {authorFilter && (
              <div className="flex items-center gap-4 bg-secondary-container text-secondary-onContainer px-6 py-3 rounded-full">
                <span className="text-label-large">Filter: Autor</span>
                <Link
                  to="/#blog-section"
                  className="hover:scale-110 transition-transform"
                  onClick={() => setIsExpanded(false)}
                  title="Filter entfernen"
                >
                  <FilterOffIcon style={{ fontSize: 20 }} />
                </Link>
              </div>
            )}
            
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="md3-btn-icon-unfilled w-12 h-12 flex items-center justify-center rounded-full bg-surface-containerHigh text-surface-on hover:bg-primary/10 hover:text-primary transition-all duration-300 shadow-elevation-1"
              aria-label={isExpanded ? "Sektion einklappen" : "Sektion ausklappen"}
            >
              <ChevronRightIcon
                className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
                sx={{ fontSize: 32 }}
              />
            </button>
          </div>
        </div>

        {/* Dynamic View: Carousel or Grid */}
        <div className="transition-all duration-500 ease-in-out">
          {!isExpanded ? (
            <ModernCarousel animated={true}>
              {displayedPosts.length === 0 ? (
                <div className="w-full text-center py-12 bg-surface-container rounded-3xl border border-dashed border-outlineVariant">
                  <p className="text-surface-onVariant">Keine Beiträge für diesen Autor gefunden.</p>
                </div>
              ) : (
                displayedPosts.map(post => renderPostCard(post))
              )}
            </ModernCarousel>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in">
              {displayedPosts.slice(0, visibleCount).map(post => renderPostCard(post))}
            </div>
          )}
        </div>

        {showMessageBox && (
          <MessageBox
            message={messageBoxContent}
            onClose={handleCloseMessageBox}
            showLoginButton={showLoginPrompt}
            onLoginRedirect={handleLoginRedirect}
          />
        )}

        {hasMore && (
          <div className="mt-16 text-center">
            <button
              onClick={handleLoadMore}
              className="md3-btn-filled rounded-full h-14 px-12 gap-3 shadow-elevation-2 hover:shadow-elevation-4 hover:scale-105 transition-all group"
            >
              {isExpanded ? 'Noch mehr laden' : 'Alle Beiträge anzeigen'}
              <ArrowRightIcon className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : 'group-hover:translate-x-1'}`} style={{ fontSize: 20 }} />
            </button>
          </div>
        )}

        {isExpanded && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsExpanded(false)}
              className="md3-btn-text text-surface-onVariant hover:text-primary transition-colors font-medium"
            >
              Weniger anzeigen
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogCards;
